import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { HOME_FAQ_ITEMS } from '../constants'
import { trackVideoOpened } from '../videoAnalytics'
import VideoDialog from './VideoDialog'

const LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s]+)/g

const TRAILING_URL_PUNCTUATION_PATTERN = /[.,;:!?)}\]」』。、]+$/u

const linkClassName = 'text-brand-600 hover:text-brand-700 underline'

function splitPlainUrl(url: string): { href: string; trailing: string } {
  const trailingMatch = url.match(TRAILING_URL_PUNCTUATION_PATTERN)
  if (!trailingMatch) {
    return { href: url, trailing: '' }
  }

  const trailing = trailingMatch[0]
  return { href: url.slice(0, -trailing.length), trailing }
}

function renderTextWithLinks(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  let lastIndex = 0
  let key = 0

  for (const match of text.matchAll(LINK_PATTERN)) {
    const [fullMatch, label, markdownUrl, plainUrl] = match
    const start = match.index ?? 0

    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start))
    }

    if (markdownUrl && label) {
      nodes.push(
        <a key={key++} href={markdownUrl} target="_blank" rel="noopener noreferrer" className={linkClassName}>
          {label}
        </a>
      )
    } else if (plainUrl) {
      const { href, trailing } = splitPlainUrl(plainUrl)
      if (href) {
        nodes.push(
          <a key={key++} href={href} target="_blank" rel="noopener noreferrer" className={linkClassName}>
            {href}
          </a>
        )
      }
      if (trailing) {
        nodes.push(trailing)
      }
    }

    lastIndex = start + fullMatch.length
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }

  return nodes.length > 0 ? nodes : [text]
}

function findFaqVideo(videoId: string | null) {
  if (!videoId) return null

  for (const item of HOME_FAQ_ITEMS) {
    if ('video' in item && item.video.id === videoId) {
      return item.video
    }
  }

  return null
}

const HomeFaq: React.FC = () => {
  const intl = useIntl()
  const [openItemId, setOpenItemId] = useState<string | null>(null)
  const [openVideoId, setOpenVideoId] = useState<string | null>(null)
  const openVideo = findFaqVideo(openVideoId)

  return (
    <>
      <section className="border-border-base rounded-xl border bg-white px-5 py-6 shadow-lg shadow-gray-900/10 sm:px-6">
        <h2 className="text-lg font-semibold text-[#002C22]">
          {intl.formatMessage({
            id: 'home.faq.heading',
            defaultMessage: 'Common questions families ask',
          })}
        </h2>
        <div className="mt-4">
          {HOME_FAQ_ITEMS.map((item, index) => {
            const isOpen = openItemId === item.id
            const answer = intl.formatMessage({ id: item.answerId, defaultMessage: item.answerDefault })
            const hasDivider = index < HOME_FAQ_ITEMS.length - 1
            const video = 'video' in item ? item.video : null

            return (
              <div key={item.id} className={hasDivider ? 'border-b border-[#E5E7EB]' : undefined}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-4 py-4 text-left text-sm font-medium text-gray-900"
                  aria-expanded={isOpen}
                  aria-controls={`home-faq-answer-${item.id}`}
                  onClick={() => setOpenItemId(isOpen ? null : item.id)}
                >
                  <span>{intl.formatMessage({ id: item.questionId, defaultMessage: item.questionDefault })}</span>
                  <img
                    src="/angle-down.svg"
                    alt=""
                    className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {isOpen ? (
                  <div id={`home-faq-answer-${item.id}`} className="pb-4 text-sm leading-6 text-gray-600">
                    {answer.split('\n\n').map((paragraph) => (
                      <p key={paragraph} className="mb-3 last:mb-0">
                        {renderTextWithLinks(paragraph)}
                      </p>
                    ))}
                    {video ? (
                      <p className="mb-3 last:mb-0">
                        {intl.formatMessage({ id: video.promptId, defaultMessage: video.promptDefault })}{' '}
                        <button
                          type="button"
                          className={`${linkClassName} cursor-pointer bg-transparent p-0`}
                          aria-label={intl.formatMessage({
                            id: video.openLabelId,
                            defaultMessage: video.openLabelDefault,
                          })}
                          onClick={() => {
                            trackVideoOpened({ videoId: video.id, locale: intl.locale })
                            setOpenVideoId(video.id)
                          }}
                        >
                          {video.title}
                        </button>
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      </section>

      {openVideo ? (
        <VideoDialog
          isOpen
          videoId={openVideo.id}
          title={openVideo.title}
          src={openVideo.src}
          closeLabel={intl.formatMessage({
            id: openVideo.closeLabelId,
            defaultMessage: openVideo.closeLabelDefault,
          })}
          backLabel={intl.formatMessage({
            id: openVideo.backLabelId,
            defaultMessage: openVideo.backLabelDefault,
          })}
          onClose={() => setOpenVideoId(null)}
        />
      ) : null}
    </>
  )
}

export default HomeFaq
